import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { toast } from "react-toastify";

import {
  cancelPendingOrderPayment,
  confirmPaymentForExistingOrder,
} from "../../actions/paymentActions";
import Meta from "../../components/Meta";
import { PAYMENT_PROVIDERS } from "../../constants/paymentConstants";
import { getSafeUserMessage } from "../../utils/errorMessages";

import "../orders/OrderScreen.css";

function safeText(value = "") {
  return String(value ?? "").trim();
}

function normalizeLower(value = "") {
  return safeText(value).toLowerCase();
}

function normalizeErrorMessage(error, fallback = "Payment could not be updated.") {
  return getSafeUserMessage(error?.normalizedMessage || error?.message, fallback);
}

function isCancelledOrFailed(searchParams) {
  const status = normalizeLower(searchParams.get("status"));
  const result = normalizeLower(searchParams.get("result"));

  return (
    status === "failed" ||
    status === "cancelled" ||
    status === "canceled" ||
    result === "failed" ||
    result === "cancelled" ||
    result === "canceled"
  );
}

export default function SumUpReturnScreen() {
  const { orderNumber: routeOrderNumber = "" } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [state, setState] = useState({
    loading: true,
    error: "",
    cancelled: false,
  });

  const orderNumber = useMemo(
    () =>
      safeText(routeOrderNumber) ||
      safeText(searchParams.get("orderNumber")) ||
      safeText(searchParams.get("order")),
    [routeOrderNumber, searchParams],
  );

  const wasCancelledOrFailed = useMemo(
    () => isCancelledOrFailed(searchParams),
    [searchParams],
  );

  useEffect(() => {
    let active = true;

    async function finishSumUpFlow() {
      if (!orderNumber) {
        setState({
          loading: false,
          error: "We could not match this SumUp return to an order.",
          cancelled: false,
        });
        return;
      }

      /* ---- Handle cancelled / failed checkouts ---- */
      if (wasCancelledOrFailed) {
        try {
          await dispatch(
            cancelPendingOrderPayment({
              orderNumber,
              reason: "Customer cancelled or payment failed via SumUp.",
            }),
          );
        } catch {
          /* best-effort cancel — order may already be cancelled */
        }

        if (!active) return;

        setState({
          loading: false,
          error: "",
          cancelled: true,
        });
        return;
      }

      /* ---- Handle successful checkouts ---- */
      try {
        const confirmed = await dispatch(
          confirmPaymentForExistingOrder({
            orderNumber,
            provider: PAYMENT_PROVIDERS.SUMUP,
            paymentProvider: PAYMENT_PROVIDERS.SUMUP,
          }),
        );

        if (!active) return;

        const confirmedOrderNumber = safeText(
          confirmed?.orderNumber || confirmed?.order?.orderNumber || orderNumber,
        );

        navigate(`/order/${encodeURIComponent(confirmedOrderNumber)}/success`, {
          replace: true,
        });
      } catch (error) {
        if (!active) return;

        const message = normalizeErrorMessage(
          error,
          "SumUp payment could not be completed.",
        );

        setState({ loading: false, error: message, cancelled: false });
        toast.error(message);
      }
    }

    finishSumUpFlow();

    return () => {
      active = false;
    };
  }, [dispatch, navigate, orderNumber, wasCancelledOrFailed]);

  return (
    <main className="order-detail">
      <Meta
        title="Completing SumUp payment | Academy Costumes London"
        robots="noindex, nofollow"
      />

      <section className="order-detail__empty">
        {state.loading ? (
          <>
            <Loader2 className="order-detail__spin" />
            <h1>Checking payment</h1>
            <p>Please wait while we retrieve the latest server status.</p>
          </>
        ) : state.cancelled ? (
          <>
            <XCircle size={28} />
            <h1>Payment cancelled</h1>
            <p>Your SumUp payment was not completed. No charge has been made.</p>
            <div className="order-detail__inline-actions">
              <Link className="order-detail__button" to="/checkout">
                Return to checkout
              </Link>
              <Link className="order-detail__button order-detail__button--dark" to="/orders">
                View orders
              </Link>
            </div>
          </>
        ) : (
          <>
            {state.error ? <XCircle size={28} /> : <CheckCircle2 size={28} />}
            <h1>Payment update</h1>
            <p>{state.error || "Your payment status is being confirmed."}</p>
            <div className="order-detail__inline-actions">
              <Link className="order-detail__button" to="/checkout">
                Return to checkout
              </Link>
              <Link className="order-detail__button order-detail__button--dark" to="/orders">
                View orders
              </Link>
            </div>
          </>
        )}
      </section>
    </main>
  );
}
